import React, { useState } from 'react';
import Logger from '../lib/Logger';
import { RootState } from '../redux/store';
import { connect, ConnectedProps } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import RoomClient from '../lib/RoomClient';
import RoomContext from '../lib/RoomContext';
import PeerView from './PeerView';
import classNames from 'classnames';
import { notify, setWebcamInProgress, setMicInProgress } from '../redux/actions';
import { NotificationType } from '../types';
import { ThunkDispatch } from 'redux-thunk';
import '../styles/Me.scss';
import { CSSTransition } from 'react-transition-group';
import MediaSettings from './MediaSettings';

const log = new Logger('Me');
log.debug('Me');

type MeProps = {
};

const mapState = (state: RootState, props: MeProps) => {
    const producerArray = Array.from(state.producers.values());

    const audioProducer = producerArray.find(
        producer => producer?.track.kind === 'audio'
    );
    const videoProducer = producerArray.find(
        producer => producer?.track.kind === 'video'
    );

    return {
        connected: state.room.state === 'connected',
        me: state.me,
        audioProducer,
        videoProducer
    };
};

const mapDispatch = (dispatch: Dispatch) => ({
    notify: (type: NotificationType, text: string, title?: string) => (dispatch as ThunkDispatch<RootState, void, AnyAction>)(notify(type, text)),
    setWebcamInProgress: (flag: boolean) => dispatch(setWebcamInProgress(flag)),
    setMicInProgress: (flag: boolean) => dispatch(setMicInProgress(flag))
});

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector> & MeProps;

type DeviceState = 'unsupported' | 'off' | 'on';

function Me(props: Props & { roomClient: RoomClient }) {
    const {
        roomClient,
        connected,
        me,
        audioProducer,
        videoProducer
    } = props;

    let micState: DeviceState;
    if (!me.canSendMic) {
        micState = 'unsupported';
    } else if (!audioProducer) {
        micState = 'unsupported';
    } else if (audioProducer.paused) {
        micState = 'off';
    } else {
        micState = 'on';
    }

    let webcamState: DeviceState;
    if (!me.canSendWebcam) {
        webcamState = 'unsupported';
    } else if (videoProducer) {
        webcamState = 'on';
    } else {
        webcamState = 'off';
    }

    // let canChangeWebcam: DeviceState;
    // if (videoProducer && me.canChangeWebcam) {
    //     canChangeWebcam = 'on';
    // } else {
    //     canChangeWebcam = 'unsupported';
    // }

    const videoVisible = Boolean(videoProducer) && !videoProducer!.paused;

    const [ settingsOpen, setSettingsOpen ] = useState<boolean>(false);
    const [ webcams, setWebcams ] = useState<MediaDeviceInfo[]>([]);
    const [ mics, setMics ] = useState<MediaDeviceInfo[]>([]);
    const [ webcamMirrored, setWebcamMirrored ] = useState<boolean>(true);
    // Resolution and codec are not backed by Redux, so keep them in local state
    // to reflect a selection immediately even when no stream is active.
    const [ activeResolution, setActiveResolution ] = useState<string>(roomClient.getActiveResolution());
    const [ activeCodec, setActiveCodec ] = useState<string>(roomClient.getActiveCodec());

    const openSettings = () => {
        Promise.all([roomClient.getWebcams(), roomClient.getMics()]).then(([webcams, mics]) => {
            setWebcams(webcams);
            setMics(mics);
            setActiveResolution(roomClient.getActiveResolution());
            setActiveCodec(roomClient.getActiveCodec());
            setSettingsOpen(true);
        });
    };

    return (
        <CSSTransition timeout={250} in appear classNames='fade-in'>
            <div data-component="Me">
                {connected && (
                    <div className="controls" onClick={e => e.stopPropagation()}>
                        <div
                            className={classNames('button', 'mic', micState, { disabled: me.micInProgress, 'in-progress': me.micInProgress })}
                            onClick={() => {
                                micState === 'on' ? roomClient.muteMic() : roomClient.unmuteMic()
                            }}
                            onContextMenu={e => {
                                openSettings();
                                e.preventDefault();
                            }}
                        />
                        <div
                            className={classNames('button', 'webcam', webcamState, { disabled: me.webcamInProgress, 'in-progress': me.webcamInProgress })}
                            onClick={() => webcamState === 'on' ? roomClient.disableWebcam() : roomClient.enableWebcam()}
                            onContextMenu={e => {
                                openSettings();
                                e.preventDefault();
                            }}
                        />
                        {webcamState === 'on' && roomClient.getActiveVideoSource() === 'webcam' && (
                        <div
                            className={classNames('button', 'webcam-mirror', { 'on': webcamMirrored })}
                            onClick={() => setWebcamMirrored(!webcamMirrored)}
                        />)}
                    </div>
                )}

                <div className={"controls settings"}>
                    <div className={"button settings"} onClick={e => {openSettings(); e.stopPropagation()}}/>
                </div>

                {settingsOpen && (
                    <MediaSettings
                        webcams={webcams.map(webcam => ({id: webcam.deviceId, label: webcam.label}))}
                        mics={mics.map(mic => ({id: mic.deviceId, label: mic.label}))}
                        videoOn={webcamState === 'on'}
                        videoSource={roomClient.getActiveVideoSource()}
                        activeWebcamId={roomClient.getActiveWebcamId()}
                        micOn={micState === 'on'}
                        activeMicId={roomClient.getActiveMicId()}
                        activeResolution={activeResolution}
                        activeCodec={activeCodec}
                        onChooseWebcam={id => roomClient.disableWebcam().then(() => roomClient.enableWebcam(id))}
                        onShareScreen={() => roomClient.enableScreenShare()}
                        onDisableWebcam={() => roomClient.disableWebcam()}
                        onChooseMic={id => roomClient.disableMic().then(() => roomClient.enableMic(id))}
                        onDisableMic={() => roomClient.muteMic()}
                        onChooseResolution={key => { setActiveResolution(key); roomClient.setResolution(key); }}
                        onChooseCodec={key => { setActiveCodec(key); roomClient.setCodec(key); }}
                        onClose={() => setSettingsOpen(false)}
                    />
                )}

                <div className={classNames('peer-view', {'has-overlay': settingsOpen})}>
                    <PeerView
                        isMe
                        videoMirrored={webcamMirrored && roomClient.getActiveVideoSource() === 'webcam'}
                        peer={me}
                        audioTrack={audioProducer?.track}
                        videoTrack={videoProducer?.track}
                        videoVisible={videoVisible}
                        videoLayer={{}}
                    />
                </div>
            </div>
        </CSSTransition>
    )
}

function MeContainer(props: Props) {
    return (
        <RoomContext.Consumer>
            {roomClient => roomClient ? <Me {...props} roomClient={roomClient} /> : <></>}
        </RoomContext.Consumer>
    );
}

export default connector(MeContainer);
